import { clamp, clampRgba, type Rgba } from "./rgba";
import { fromOklab, fromPolar, toPolar, type Vector } from "./spaces";

// What a box does with a colour sRGB cannot show, which is what the palette does with a partner: the same search.
export function intoGamut(oklab: Vector, a: number): Rgba {
  const [l, c, h] = toPolar(oklab, 0);
  return inGamut(l, c, h, a);
}

// Chroma is what gives way when a hue or a lightness leaves sRGB, because clipping a channel moves the hue instead —
// a red rotated to a yellow at full chroma comes back as a different yellow depending on which channel hit the wall.
// Grey is always inside, so the search always has a floor to fall back to.
export function inGamut(lightness: number, chroma: number, hue: number, a: number): Rgba {
  const l = clamp(lightness, 0, 1);
  const h = ((hue % 360) + 360) % 360;
  const paint = (c: number) => fromOklab(fromPolar([l, c, h]));
  const asked = paint(chroma);
  if (holds(asked)) return clampRgba({ ...asked, a });

  let low = 0;
  let high = chroma;
  for (let step = 0; step < GAMUT_STEPS; step++) {
    const middle = (low + high) / 2;
    if (holds(paint(middle))) low = middle;
    else high = middle;
  }
  return clampRgba({ ...paint(low), a });
}

// Twenty halvings land within a millionth of a chroma unit of the boundary, far finer than eight bits can tell.
const GAMUT_STEPS = 20;

// Half a level, since what comes out of the search is rounded to eight bits anyway and half a level rounds inside.
function holds({ r, g, b }: { r: number; g: number; b: number }): boolean {
  return [r, g, b].every((channel) => channel >= -0.5 && channel <= 255.5);
}
